import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getStatsChartVisibility } from "@/pages/stats/stats-chart-settings";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export const QUESTION_CHART_PREFERENCES_VERSION = 1;
export const QUESTION_OPTION_LABEL_MAX_LENGTH = 20;

export const truncateQuestionOptionLabel = (
  value,
  maxLength = QUESTION_OPTION_LABEL_MAX_LENGTH,
) => {
  const label = String(value ?? "").trim();
  const characters = Array.from(label);
  const safeMaxLength = Math.max(1, Number(maxLength) || 1);

  if (characters.length <= safeMaxLength) {
    return label;
  }

  if (safeMaxLength === 1) {
    return "…";
  }

  return `${characters
    .slice(0, safeMaxLength - 1)
    .join("")
    .trimEnd()}…`;
};

const asArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === "object") {
    for (const wrapperKey of ["data", "items", "members", "results"]) {
      if (Array.isArray(value[wrapperKey])) {
        return value[wrapperKey];
      }
    }

    const objectValues = Object.values(value);
    if (
      objectValues.length > 0 &&
      objectValues.every(
        (entry) => entry && typeof entry === "object" && !Array.isArray(entry),
      )
    ) {
      return objectValues;
    }

    return [value];
  }

  return [];
};

const getQuestionOptions = (question) => {
  if (!question || typeof question !== "object") {
    return [];
  }

  for (const optionsKey of ["options", "questionOptions", "question_options"]) {
    const rawOptions = question[optionsKey];

    if (Array.isArray(rawOptions)) {
      return rawOptions;
    }

    if (rawOptions && typeof rawOptions === "object") {
      const wrapped = asArray(rawOptions);
      if (wrapped.length > 0) {
        return wrapped;
      }
    }
  }

  return [];
};

const normalizeLocalizedEntry = (entry) => {
  if (typeof entry === "string" || typeof entry === "number") {
    return { language: undefined, text: String(entry) };
  }

  if (entry && typeof entry === "object") {
    const text = entry.text ?? entry.label ?? entry.value ?? entry.name;
    return {
      language: entry.language ?? entry.lang ?? entry.locale,
      text:
        typeof text === "string" || typeof text === "number"
          ? String(text)
          : undefined,
    };
  }

  return { language: undefined, text: undefined };
};

const getLocalizedText = (entries, language, fallback = "") => {
  if (typeof entries === "string" || typeof entries === "number") {
    const text = String(entries).trim();
    return text !== "" ? text : fallback;
  }

  if (!entries || typeof entries !== "object") {
    return fallback;
  }

  if (!Array.isArray(entries)) {
    if (typeof entries.text === "string" && entries.text.trim() !== "") {
      return entries.text;
    }

    const languageKey = String(language ?? "").split("-")[0];
    const candidateKeys = [language, languageKey, "es", "en"];
    for (const candidateKey of candidateKeys) {
      const candidateValue = candidateKey ? entries[candidateKey] : undefined;
      if (typeof candidateValue === "string" && candidateValue.trim() !== "") {
        return candidateValue;
      }
    }

    const firstString = Object.values(entries).find(
      (value) => typeof value === "string" && value.trim() !== "",
    );
    return typeof firstString === "string" ? firstString : fallback;
  }

  const normalizedEntries = entries.map(normalizeLocalizedEntry);
  const normalizedLanguage = String(language ?? "").split("-")[0];
  return (
    normalizedEntries.find((entry) => entry?.language === language)?.text ??
    normalizedEntries.find((entry) => entry?.language === normalizedLanguage)
      ?.text ??
    normalizedEntries.find((entry) => entry?.language === "es")?.text ??
    normalizedEntries.find(
      (entry) => typeof entry?.text === "string" && entry.text.trim() !== "",
    )?.text ??
    fallback
  );
};

const normalizeMatchValue = (value) => {
  try {
    if (value && typeof value === "object") {
      const objectIdentity =
        value.id ?? value.value ?? value.text ?? value.label ?? value.name;
      if (objectIdentity !== null && objectIdentity !== undefined) {
        return normalizeMatchValue(objectIdentity);
      }
      return "";
    }

    return String(value ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{M}/gu, "");
  } catch (error) {
    return "";
  }
};

const getQuestionIdentity = (question) => {
  if (question === null || question === undefined) {
    return null;
  }

  if (typeof question === "string" || typeof question === "number") {
    const text = String(question).trim();
    return text !== "" ? question : null;
  }

  if (typeof question !== "object") {
    return null;
  }

  const identity = question.id ?? question.alias ?? question.key ?? null;
  if (identity === null || identity === undefined) {
    return null;
  }

  if (typeof identity === "string" && identity.trim() === "") {
    return null;
  }

  return identity;
};

const getQuestionMatchIdentities = (question) => {
  try {
    if (question === null || question === undefined) {
      return new Set();
    }

    if (typeof question === "string" || typeof question === "number") {
      const normalized = normalizeMatchValue(question);
      return normalized ? new Set([normalized]) : new Set();
    }

    if (typeof question !== "object") {
      return new Set();
    }

    const rawIdentities = [question.id, question.alias, question.key];
    const normalized = rawIdentities
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => normalizeMatchValue(value))
      .filter((value) => value !== "");

    return new Set(normalized);
  } catch (error) {
    return new Set();
  }
};

const getOptionIdentity = (option, index) => {
  if (option && typeof option === "object") {
    const identity = option.id ?? option.value ?? option.key;
    if (
      identity !== null &&
      identity !== undefined &&
      String(identity).trim() !== ""
    ) {
      return identity;
    }
  }

  if (
    (typeof option === "string" || typeof option === "number") &&
    String(option).trim() !== ""
  ) {
    return option;
  }

  return `option-${index}`;
};

const getAnswerCandidateValues = (answer) => {
  try {
    if (!answer || typeof answer !== "object") {
      return new Set();
    }

    const values = [
      answer.answerText,
      answer.answer,
      answer.value,
      answer.text,
    ];
    const selectedOption =
      answer.questionOption ?? answer.question_option ?? answer.option;

    if (selectedOption && typeof selectedOption === "object") {
      values.push(
        selectedOption.id,
        selectedOption.value,
        selectedOption.text,
        selectedOption.label,
        selectedOption.name,
      );
    } else {
      values.push(selectedOption);
    }

    const normalized = values
      .filter((value) => {
        if (value === null || value === undefined) {
          return false;
        }
        if (typeof value === "object") {
          return false;
        }
        return String(value).trim() !== "";
      })
      .map((value) => normalizeMatchValue(value))
      .filter((value) => value !== "");

    return new Set(normalized);
  } catch (error) {
    return new Set();
  }
};

const getOptionMatchValues = (option) => {
  try {
    if (option === null || option === undefined) {
      return new Set();
    }

    if (typeof option === "string" || typeof option === "number") {
      const normalized = normalizeMatchValue(option);
      return normalized ? new Set([normalized]) : new Set();
    }

    if (typeof option !== "object") {
      return new Set();
    }

    const rawTexts = asArray(option.text ?? option.label ?? option.name);
    const translatedTexts = rawTexts.map((entry) => {
      if (typeof entry === "string" || typeof entry === "number") {
        return entry;
      }
      return entry?.text ?? entry?.label ?? entry?.value;
    });

    const normalized = [option.id, option.value, option.key, ...translatedTexts]
      .filter((value) => {
        if (value === null || value === undefined) {
          return false;
        }
        if (typeof value === "object") {
          return false;
        }
        return String(value).trim() !== "";
      })
      .map((value) => normalizeMatchValue(value))
      .filter((value) => value !== "");

    return new Set(normalized);
  } catch (error) {
    return new Set();
  }
};

const normalizeCollection = (value) => {
  try {
    if (Array.isArray(value)) {
      return value;
    }

    if (value && typeof value === "object") {
      for (const wrapperKey of [
        "data",
        "items",
        "members",
        "results",
        "communications",
        "questions",
        "answers",
      ]) {
        if (Array.isArray(value[wrapperKey])) {
          return value[wrapperKey];
        }
      }

      return [];
    }

    return [];
  } catch (error) {
    return [];
  }
};

const findSelectedChartOption = (options, answer) => {
  const candidateValues = getAnswerCandidateValues(answer);
  return options.find((option) =>
    [...option.matchValues].some((value) => candidateValues.has(value)),
  );
};

const findCatalogChartForIdentities = (
  chartMap,
  communicationType,
  identities,
) => {
  if (!identities || identities.size === 0) {
    return null;
  }

  for (const candidateChart of chartMap.values()) {
    if (candidateChart.communicationType !== communicationType) {
      continue;
    }

    for (const identity of identities) {
      if (candidateChart.questionMatchIdentities.has(identity)) {
        return candidateChart;
      }
    }
  }

  return null;
};

const registerQuestionChart = ({
  chartMap,
  question,
  communicationType,
  language,
  typeLabels,
}) => {
  try {
    if (!question || typeof question !== "object") {
      return null;
    }

    const options = getQuestionOptions(question).filter(
      (option) => option !== null && option !== undefined,
    );
    const identity = getQuestionIdentity(question);

    if (identity === null || options.length === 0) {
      return null;
    }

    const questionIdentities = getQuestionMatchIdentities(question);
    if (questionIdentities.size === 0) {
      return null;
    }

    const duplicateChart = findCatalogChartForIdentities(
      chartMap,
      communicationType,
      questionIdentities,
    );
    if (duplicateChart) {
      return duplicateChart;
    }

    const key = `${communicationType}:${String(identity)}`;
    if (chartMap.has(key)) {
      return chartMap.get(key);
    }

    const rawTitle =
      (typeof question.label === "string" && question.label.trim() !== ""
        ? question.label
        : getLocalizedText(
            question.texts,
            language,
            typeof question.alias === "string" && question.alias.trim() !== ""
              ? question.alias
              : String(identity),
          )) || String(identity);
    const typeLabel =
      typeLabels && typeof typeLabels[communicationType] === "string"
        ? typeLabels[communicationType]
        : communicationType;
    const rawOrder = Number(question.order ?? 0);
    const usedOptionKeys = new Set();

    chartMap.set(key, {
      key,
      communicationType,
      order: Number.isFinite(rawOrder) ? rawOrder : 0,
      questionMatchIdentities: questionIdentities,
      title: `${String(rawTitle)} — ${String(typeLabel)}`,
      data: options.map((option, index) => {
        const rawOptionKey = String(getOptionIdentity(option, index));
        let optionKey = rawOptionKey;
        let suffix = 1;
        while (usedOptionKeys.has(optionKey)) {
          suffix += 1;
          optionKey = `${rawOptionKey}__${suffix}`;
        }
        usedOptionKeys.add(optionKey);

        const optionObject = option && typeof option === "object" ? option : {};
        const fallbackName =
          typeof option === "string" || typeof option === "number"
            ? String(option)
            : String(
                optionObject.value ?? optionObject.id ?? optionObject.key ?? "",
              );
        const optionName =
          getLocalizedText(
            optionObject.text ?? optionObject.label ?? option,
            language,
            fallbackName,
          ) ||
          fallbackName ||
          optionKey;

        return {
          key: optionKey,
          name: String(optionName),
          value: 0,
          matchValues: getOptionMatchValues(option),
        };
      }),
    });

    return chartMap.get(key);
  } catch (error) {
    return null;
  }
};

const countCommunicationAnswers = ({
  chartMap,
  communications,
  communicationType,
}) => {
  try {
    const normalizedCommunications = normalizeCollection(communications);

    for (const communication of normalizedCommunications) {
      if (!communication || typeof communication !== "object") {
        continue;
      }

      for (const answer of normalizeCollection(communication.answers)) {
        if (!answer || typeof answer !== "object") {
          continue;
        }

        const answerQuestionIdentities = getQuestionMatchIdentities(
          answer.question,
        );
        if (answerQuestionIdentities.size === 0) {
          continue;
        }

        // Solo se contabiliza hacia preguntas del catálogo del tenant.
        // Las respuestas huérfanas (variantes históricas, preguntas eliminadas
        // o shapes inesperados) se ignoran y nunca crean gráficos nuevos.
        const chart = findCatalogChartForIdentities(
          chartMap,
          communicationType,
          answerQuestionIdentities,
        );
        if (!chart) {
          continue;
        }

        try {
          const selectedOption = findSelectedChartOption(chart.data, answer);
          if (selectedOption) {
            selectedOption.value += 1;
          }
        } catch (error) {
          continue;
        }
      }
    }
  } catch (error) {
    // Nunca romper la página por respuestas inesperadas.
  }
};

const normalizeQuestionsByType = (questionsByType) => {
  const normalized = { complaint: [], consultation: [] };

  try {
    if (!questionsByType || typeof questionsByType !== "object") {
      return normalized;
    }

    const source = Array.isArray(questionsByType)
      ? { complaint: questionsByType }
      : questionsByType;

    const pickList = (candidate) => {
      if (Array.isArray(candidate)) {
        return candidate;
      }
      if (candidate && typeof candidate === "object") {
        for (const wrapperKey of [
          "data",
          "items",
          "members",
          "results",
          "questions",
        ]) {
          if (Array.isArray(candidate[wrapperKey])) {
            return candidate[wrapperKey];
          }
        }
      }
      return [];
    };

    const complaintCandidates = [
      source.complaint,
      source.complaints,
      source.denuncia,
      source.denuncias,
    ];
    const consultationCandidates = [
      source.consultation,
      source.consultations,
      source.inquiry,
      source.inquiries,
      source.query,
      source.queries,
      source.consulta,
      source.consultas,
    ];

    for (const candidate of complaintCandidates) {
      const list = pickList(candidate);
      if (list.length > 0) {
        normalized.complaint = list;
        break;
      }
    }

    for (const candidate of consultationCandidates) {
      const list = pickList(candidate);
      if (list.length > 0) {
        normalized.consultation = list;
        break;
      }
    }

    if (
      normalized.complaint.length === 0 &&
      normalized.consultation.length === 0
    ) {
      const fallbackList = pickList(source);
      if (fallbackList.length > 0) {
        normalized.complaint = fallbackList;
      }
    }
  } catch (error) {
    // Devolver catálogos vacíos antes que romper.
  }

  return normalized;
};

const normalizeCommunicationsByType = (communicationsByType) => {
  const normalized = { complaint: [], consultation: [] };

  try {
    if (!communicationsByType || typeof communicationsByType !== "object") {
      return normalized;
    }

    const pickList = (candidate) => normalizeCollection(candidate);

    normalized.complaint = pickList(
      communicationsByType.complaint ?? communicationsByType.complaints,
    );
    normalized.consultation = pickList(
      communicationsByType.consultation ??
        communicationsByType.consultations ??
        communicationsByType.inquiry ??
        communicationsByType.inquiries ??
        communicationsByType.query ??
        communicationsByType.queries,
    );
  } catch (error) {
    // Devolver listas vacías antes que romper.
  }

  return normalized;
};

const finalizeQuestionCharts = (chartMap) => {
  const communicationTypes = ["complaint", "consultation"];
  try {
    return [...chartMap.values()]
      .sort((left, right) => {
        try {
          const typeDifference =
            communicationTypes.indexOf(left.communicationType) -
            communicationTypes.indexOf(right.communicationType);
          const orderDifference =
            (Number(left.order) || 0) - (Number(right.order) || 0);
          const titleDifference = String(left.title ?? "").localeCompare(
            String(right.title ?? ""),
          );
          return typeDifference || orderDifference || titleDifference;
        } catch (error) {
          return 0;
        }
      })
      .map((chart) => {
        try {
          const data = Array.isArray(chart.data)
            ? chart.data.map(({ matchValues, ...option }) => ({
                key: String(option.key ?? ""),
                name: String(option.name ?? ""),
                value: Number(option.value) || 0,
              }))
            : [];
          const totalAnswers = data.reduce(
            (total, option) => total + (Number(option.value) || 0),
            0,
          );
          return {
            key: String(chart.key ?? ""),
            communicationType: chart.communicationType,
            order: Number(chart.order) || 0,
            title: String(chart.title ?? ""),
            data,
            totalAnswers,
            hasData: totalAnswers > 0,
          };
        } catch (error) {
          return null;
        }
      })
      .filter((chart) => chart && chart.key !== "");
  } catch (error) {
    return [];
  }
};

/**
 * Acumulador incremental para calcular gráficos sin mantener el universo
 * completo en memoria: se registran las preguntas una vez y se alimentan
 * lotes acotados de comunicaciones (p. ej. páginas de 100) que se liberan
 * tras contar. Equivale a buildQuestionOptionCharts con los mismos datos.
 */
export const createQuestionChartAccumulator = ({
  questionsByType,
  language,
  typeLabels,
} = {}) => {
  const chartMap = new Map();
  const communicationTypes = ["complaint", "consultation"];
  const normalizedQuestions = normalizeQuestionsByType(questionsByType);
  const safeLanguage = typeof language === "string" ? language : "es";
  const safeTypeLabels =
    typeLabels && typeof typeLabels === "object" ? typeLabels : {};

  for (const communicationType of communicationTypes) {
    for (const question of normalizeCollection(
      normalizedQuestions[communicationType],
    )) {
      registerQuestionChart({
        chartMap,
        question,
        communicationType,
        language: safeLanguage,
        typeLabels: safeTypeLabels,
      });
    }
  }

  return {
    feed(communications, communicationType) {
      countCommunicationAnswers({
        chartMap,
        communications,
        communicationType,
      });
    },
    build() {
      return finalizeQuestionCharts(chartMap);
    },
  };
};

export const buildQuestionOptionCharts = ({
  communicationsByType,
  questionsByType,
  language,
  typeLabels,
}) => {
  try {
    const accumulator = createQuestionChartAccumulator({
      questionsByType,
      language,
      typeLabels,
    });
    const normalizedCommunications =
      normalizeCommunicationsByType(communicationsByType);
    for (const communicationType of ["complaint", "consultation"]) {
      accumulator.feed(
        normalizedCommunications[communicationType],
        communicationType,
      );
    }
    return accumulator.build();
  } catch (error) {
    return [];
  }
};

export const getQuestionChartStorageKey = (user) => {
  try {
    const rawIdentity =
      user && typeof user === "object"
        ? user.id ?? user.email ?? "anonymous"
        : user ?? "anonymous";
    const userIdentity =
      typeof rawIdentity === "string" || typeof rawIdentity === "number"
        ? String(rawIdentity).trim() !== ""
          ? String(rawIdentity)
          : "anonymous"
        : "anonymous";
    return `canaletico:stats-question-charts:v${QUESTION_CHART_PREFERENCES_VERSION}:${userIdentity}`;
  } catch (error) {
    return `canaletico:stats-question-charts:v${QUESTION_CHART_PREFERENCES_VERSION}:anonymous`;
  }
};

const getQuestionOptionLabel = (payload, fallback = "") =>
  String(payload?.[0]?.payload?.name ?? fallback ?? "").trim();

const QuestionOptionValueTooltip = ({
  active,
  payload,
  label,
  answerLabel,
}) => {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const optionLabel = getQuestionOptionLabel(payload, label);
  const value = Number(payload[0]?.value) || 0;

  return (
    <div
      role="tooltip"
      data-testid="question-option-value-tooltip"
      className="max-w-80 rounded-lg border bg-background px-3 py-2 text-xs shadow-xl"
    >
      <p className="whitespace-normal break-words font-medium text-foreground">
        {optionLabel}
      </p>
      <p className="mt-1 text-muted-foreground">
        {answerLabel}:{" "}
        <span className="font-mono font-medium text-foreground">{value}</span>
      </p>
    </div>
  );
};

const QuestionOptionLabelPopup = ({ hoveredLabel }) => {
  if (!hoveredLabel) {
    return null;
  }

  return (
    <div
      role="tooltip"
      data-testid="question-option-label-tooltip"
      className="pointer-events-none fixed z-50 max-w-80 rounded-lg border bg-background px-3 py-2 text-xs font-medium text-foreground shadow-xl"
      style={{ left: hoveredLabel.x, top: hoveredLabel.y }}
    >
      <span className="whitespace-normal break-words">
        {hoveredLabel.label}
      </span>
    </div>
  );
};

const QuestionOptionAxisTick = ({
  x,
  y,
  payload,
  data,
  onLabelMouseEnter,
  onLabelMouseMove,
  onLabelMouseLeave,
  maxLabelLength,
}) => {
  const fullLabel = String(payload?.value ?? "").trim();
  const truncatedLabel = truncateQuestionOptionLabel(fullLabel, maxLabelLength);
  const optionValue = Number(data?.[payload?.index]?.value) || 0;

  return (
    <g
      transform={`translate(${x},${y})`}
      data-testid="question-option-axis-label"
      data-full-label={fullLabel}
      data-truncated={truncatedLabel !== fullLabel ? "true" : "false"}
      data-option-index={payload?.index}
      data-option-value={optionValue}
      onMouseEnter={(event) => onLabelMouseEnter(fullLabel, event)}
      onMouseMove={(event) => onLabelMouseMove(fullLabel, event)}
      onMouseLeave={onLabelMouseLeave}
      className="cursor-help"
    >
      <title>{fullLabel}</title>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="hsl(var(--muted-foreground))"
        fontSize={12}
      >
        {truncatedLabel}
      </text>
    </g>
  );
};

const QuestionOptionLegend = ({
  payload,
  onLabelMouseEnter,
  onLabelMouseMove,
  onLabelMouseLeave,
}) => (
  <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 pt-2 text-xs text-muted-foreground">
    {(Array.isArray(payload) ? payload : []).map((entry) => {
      const fullLabel = String(entry?.value ?? "").trim();
      const truncatedLabel = truncateQuestionOptionLabel(fullLabel);

      return (
        <li
          key={`${entry?.dataKey ?? "option"}:${fullLabel}`}
          data-testid="question-option-legend-label"
          data-full-label={fullLabel}
          data-truncated={truncatedLabel !== fullLabel ? "true" : "false"}
          className="flex cursor-help items-center gap-1.5"
          onMouseEnter={(event) => onLabelMouseEnter(fullLabel, event)}
          onMouseMove={(event) => onLabelMouseMove(fullLabel, event)}
          onMouseLeave={onLabelMouseLeave}
        >
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 shrink-0 rounded-sm"
            style={{ backgroundColor: entry?.color }}
          />
          <span>{truncatedLabel}</span>
        </li>
      );
    })}
  </ul>
);

const QuestionChart = ({ chart, chartType, colors }) => {
  const { t } = useTranslation();
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const safeData = Array.isArray(chart?.data) ? chart.data : [];
  const safeColors =
    Array.isArray(colors) && colors.length > 0
      ? colors
      : ["hsl(var(--chart-1))"];
  const hasAnswers = safeData.some((entry) => Number(entry?.value) > 0);
  const answerLabel = t("statsPage.questionCharts.answers");
  const availableWidthPerOption =
    safeData.length > 0 ? Math.max(0, chartWidth - 24) / safeData.length : 0;
  const axisLabelMaxLength = Math.max(
    3,
    Math.min(
      QUESTION_OPTION_LABEL_MAX_LENGTH,
      Math.floor((availableWidthPerOption - 8) / 7.5),
    ),
  );

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) {
      return undefined;
    }

    const updateWidth = () => setChartWidth(container.clientWidth);
    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  const showLabelPopup = (label, event) => {
    const popupWidth = 320;
    const popupHeight = 96;
    setHoveredLabel({
      label,
      x: Math.max(
        8,
        Math.min(event.clientX + 12, window.innerWidth - popupWidth - 8),
      ),
      y: Math.max(
        8,
        Math.min(event.clientY + 12, window.innerHeight - popupHeight - 8),
      ),
    });
  };
  const hideLabelPopup = () => setHoveredLabel(null);
  const renderPieLabel = ({ name, value, x, y, textAnchor }) => {
    if (Number(value) <= 0) {
      return null;
    }

    const fullLabel = String(name ?? "").trim();
    const truncatedLabel = truncateQuestionOptionLabel(fullLabel);

    return (
      <g
        data-testid="question-option-pie-label"
        data-full-label={fullLabel}
        data-truncated={truncatedLabel !== fullLabel ? "true" : "false"}
        onMouseEnter={(event) => showLabelPopup(fullLabel, event)}
        onMouseMove={(event) => showLabelPopup(fullLabel, event)}
        onMouseLeave={hideLabelPopup}
        className="cursor-help"
      >
        <title>{fullLabel}</title>
        <text
          x={x}
          y={y}
          textAnchor={textAnchor}
          dominantBaseline="central"
          fill="hsl(var(--foreground))"
          fontSize={12}
        >
          {`${truncatedLabel} (${value})`}
        </text>
      </g>
    );
  };

  if (chartType === "pie" && !hasAnswers) {
    return (
      <div className="flex h-[400px] items-center justify-center text-center text-muted-foreground">
        {t("statsPage.questionCharts.noAnswers")}
      </div>
    );
  }

  return (
    <div ref={chartContainerRef} className="relative h-[400px] w-full">
      <QuestionOptionLabelPopup hoveredLabel={hoveredLabel} />
      <ResponsiveContainer width="100%" height="100%">
        {chartType === "pie" ? (
          <PieChart>
            <Pie
              data={safeData}
              cx="50%"
              cy="50%"
              dataKey="value"
              label={renderPieLabel}
              labelLine={true}
              outerRadius={150}
              paddingAngle={5}
            >
              {safeData.map((entry, index) => (
                <Cell
                  key={entry.key}
                  fill={safeColors[index % safeColors.length]}
                />
              ))}
            </Pie>
            <Tooltip
              content={<QuestionOptionValueTooltip answerLabel={answerLabel} />}
            />
            <Legend
              content={
                <QuestionOptionLegend
                  onLabelMouseEnter={showLabelPopup}
                  onLabelMouseMove={showLabelPopup}
                  onLabelMouseLeave={hideLabelPopup}
                />
              }
            />
          </PieChart>
        ) : (
          <BarChart
            data={safeData}
            margin={{ top: 32, right: 16, left: 8, bottom: 24 }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="name"
              interval={0}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tick={
                <QuestionOptionAxisTick
                  data={safeData}
                  onLabelMouseEnter={showLabelPopup}
                  onLabelMouseMove={showLabelPopup}
                  onLabelMouseLeave={hideLabelPopup}
                  maxLabelLength={axisLabelMaxLength}
                />
              }
            />
            <YAxis
              allowDecimals={false}
              domain={[0, "dataMax + 1"]}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              content={<QuestionOptionValueTooltip answerLabel={answerLabel} />}
            />
            <Bar
              dataKey="value"
              name={answerLabel}
              fill="hsl(var(--primary))"
              radius={[4, 4, 0, 0]}
            >
              <LabelList
                dataKey="value"
                position="top"
                fill="#000000"
                formatter={(value) => (value === 0 ? "" : value)}
              />
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const QuestionOptionCharts = ({ charts, colors, preferences }) => {
  const { t } = useTranslation();
  const safeCharts = Array.isArray(charts) ? charts : [];
  const safePreferences =
    preferences && typeof preferences === "object" ? preferences : {};
  const safeColors =
    Array.isArray(colors) && colors.length > 0
      ? colors
      : ["hsl(var(--chart-1))"];

  if (safeCharts.length === 0) {
    return null;
  }

  return (
    <>
      {safeCharts.map((chart) => {
        if (!chart || typeof chart.key !== "string" || chart.key === "") {
          return null;
        }
        const preference = safePreferences[chart.key] ?? {};
        let isVisible = false;
        try {
          isVisible = getStatsChartVisibility(safePreferences, {
            key: chart.key,
            defaultVisible: Boolean(chart.hasData),
          });
        } catch (error) {
          isVisible = Boolean(chart.hasData);
        }
        if (!isVisible) {
          return null;
        }

        const chartType = preference.type === "pie" ? "pie" : "bar";
        return (
          <section
            key={chart.key}
            className="my-4"
            data-testid="question-option-chart"
            data-chart-key={chart.key}
            data-chart-type={chartType}
            data-total-answers={chart.totalAnswers}
            data-has-data={chart.hasData ? "true" : "false"}
          >
            <Card>
              <CardHeader>
                <CardTitle>{chart.title}</CardTitle>
                <CardDescription>
                  {t("statsPage.questionCharts.description")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <QuestionChart
                  chart={chart}
                  chartType={chartType}
                  colors={safeColors}
                />
              </CardContent>
            </Card>
          </section>
        );
      })}
    </>
  );
};

export default QuestionOptionCharts;
